import { sourceChannels } from "@purlink/shared/attribution";
import { followUpFilters, leadSorts, type MoveLeadInput } from "@purlink/shared/leads-api";
import { useInfiniteQuery, useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Link, createFileRoute, useNavigate } from "@tanstack/react-router";
import {
  ArrowDownToLine,
  ArrowUpToLine,
  ChevronDown,
  ChevronUp,
  Eye,
  EyeOff,
  ExternalLink,
  ListChecks,
  MoreHorizontal,
  Plus,
  Search,
  Send,
  SlidersHorizontal,
  Trash2,
  UserPlus,
} from "lucide-react";
import { useEffect, useState } from "react";
import { z } from "zod";
import { PageHeader } from "~/components/shell/app-shell";
import { StageBadge, type StageColor } from "~/components/ui/badge";
import { Button } from "~/components/ui/button";
import { Card } from "~/components/ui/card";
import { ConfirmDialog } from "~/components/ui/confirm-dialog";
import { EmptyState, Skeleton } from "~/components/ui/feedback";
import { Highlight } from "~/components/ui/highlight";
import { Input } from "~/components/ui/input";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "~/components/ui/menu";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "~/components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "~/components/ui/table";
import { toast } from "~/components/ui/toaster";
import {
  leadActions,
  leadKeys,
  leadListQuery,
  servicesQuery,
  stagesQuery,
  type LeadFilters,
  type LeadSummary,
} from "~/features/leads/api";
import { AssignLeadDialog } from "~/features/leads/assign-dialog";
import {
  ActiveFilterChips,
  countFilters,
  FilterDialog,
  pickFilters,
  type LeadFilterValues,
} from "~/features/leads/filter-dialog";
import { HiddenMatches } from "~/features/leads/hidden-matches";
import { ChannelBadge, EmailFlagBadge, FollowUpBadge } from "~/features/leads/lead-badges";
import { AssigneeAvatars, CampaignTags, UnreviewedDot } from "~/features/leads/lead-markers";
import { MarkAllReviewedButton } from "~/features/leads/mark-all-reviewed";
import { useLeadReview } from "~/features/leads/use-lead-review";
import { useComposeEmail } from "~/features/emails/lazy-compose";
import { LeadFormDialog } from "~/features/leads/lead-form-dialog";
import { LeadPeek } from "~/features/leads/lead-peek-card";
import { errorMessage } from "~/lib/auth";
import { formatDate, fullName } from "~/lib/format";
import { useCan } from "~/lib/use-can";
import { cn } from "~/lib/utils";

const optional = z.string().optional().catch(undefined);
const flag = z.boolean().optional().catch(undefined);
const day = z.iso.date().optional().catch(undefined);

const searchSchema = z.object({
  q: optional,
  stageId: optional,
  serviceId: optional,
  channel: z.enum(sourceChannels).optional().catch(undefined),
  campaign: optional,
  followUp: z.enum(followUpFilters).optional().catch(undefined),
  mine: flag,
  unreviewed: flag,
  assignee: optional,
  campaignId: optional,
  keyword: optional,
  createdFrom: day,
  createdTo: day,
  sort: z.enum(leadSorts).optional().catch(undefined),
});
type Search = z.infer<typeof searchSchema>;

export const Route = createFileRoute("/_app/leads/")({
  validateSearch: searchSchema,
  component: LeadsPage,
});

const sortLabels: Record<(typeof leadSorts)[number], string> = {
  priority: "Priority",
  newest: "Newest first",
  oldest: "Oldest first",
  name: "Name (A–Z)",
};

function LeadsPage() {
  const search = Route.useSearch();
  const navigate = useNavigate({ from: Route.fullPath });
  const queryClient = useQueryClient();
  const [addOpen, setAddOpen] = useState(false);
  const [filtersOpen, setFiltersOpen] = useState(false);
  const [deleting, setDeleting] = useState<LeadSummary | null>(null);
  const [assigning, setAssigning] = useState<LeadSummary | null>(null);
  const [query, setQuery] = useState(search.q ?? "");
  const can = useCan();
  const { compose, dialog: composeDialog } = useComposeEmail();
  const onSendEmail = can("email", "send") ? compose : undefined;
  const onAssign = can("lead", "assign") ? setAssigning : undefined;
  const onDelete = can("lead", "delete") ? setDeleting : undefined;
  const review = useLeadReview();

  const stages = useQuery(stagesQuery);
  const services = useQuery(servicesQuery);
  const sort = search.sort ?? "priority";
  const filters: LeadFilters = { ...search, sort };
  const leads = useInfiniteQuery(leadListQuery(filters));
  const rows = leads.data?.pages.flatMap((page) => page.leads) ?? [];
  const total = leads.data?.pages[0]?.total ?? 0;
  const unreviewedTotal = leads.data?.pages[0]?.unreviewedTotal ?? 0;

  const setSearch = (patch: Partial<Search>) =>
    navigate({ search: (prev) => ({ ...prev, ...patch }), replace: true });

  // Debounced search box → URL.
  useEffect(() => {
    const handle = setTimeout(() => {
      if ((search.q ?? "") !== query.trim()) void setSearch({ q: query.trim() || undefined });
    }, 300);
    return () => clearTimeout(handle);
    // oxlint-disable-next-line react-hooks/exhaustive-deps -- only react to typing
  }, [query]);

  const applied = pickFilters(search);
  const activeFilterCount = countFilters(applied);
  const canReorder = sort === "priority";

  const move = useMutation({
    mutationFn: ({ lead, ...position }: { lead: LeadSummary } & MoveLeadInput) =>
      leadActions.move(lead.id, position),
    onSuccess: async (result, { lead }) => {
      await queryClient.invalidateQueries({ queryKey: leadKeys.all });
      toast.success(`${fullName(lead)} is now priority ${result.priority}.`);
    },
    onError: (error) => toast.error(errorMessage(error)),
  });

  const remove = useMutation({
    mutationFn: (lead: LeadSummary) => leadActions.remove(lead.id),
    onSuccess: async (_data, lead) => {
      await queryClient.invalidateQueries({ queryKey: leadKeys.all });
      toast.success(`${fullName(lead)} was deleted.`);
      setDeleting(null);
    },
    onError: (error) => toast.error(errorMessage(error)),
  });

  const reorder = (lead: LeadSummary, to: "top" | "up" | "down" | "bottom") => {
    const index = rows.findIndex((row) => row.id === lead.id);
    const neighbours: MoveLeadInput = {
      top: { to: "top" as const },
      up: { beforeId: rows[index - 2]?.id ?? null, afterId: rows[index - 1]?.id ?? null },
      down: { beforeId: rows[index + 1]?.id ?? null, afterId: rows[index + 2]?.id ?? null },
      bottom: { to: "bottom" as const },
    }[to];
    move.mutate({ lead, ...neighbours });
  };

  const stageList = stages.data?.stages ?? [];
  const serviceList = services.data?.services ?? [];
  const applyFilters = (next: LeadFilterValues) => void setSearch(next);

  return (
    <>
      <PageHeader
        title="All Leads"
        description={
          leads.isPending
            ? "Every enquiry, ordered by priority."
            : `${total} lead${total === 1 ? "" : "s"}${activeFilterCount || search.q ? " match your filters" : ""}.`
        }
        actions={
          <Button size="sm" onClick={() => setAddOpen(true)}>
            <Plus /> Add lead
          </Button>
        }
      />

      <Card padding="sm" className="mb-4 flex flex-col gap-3">
        <div className="flex flex-col gap-2 sm:flex-row sm:items-center">
          <div className="relative flex-1">
            <Search
              className="pointer-events-none absolute top-1/2 left-3 size-4 -translate-y-1/2 text-fg-muted"
              aria-hidden
            />
            <Input
              type="search"
              value={query}
              onChange={(event) => setQuery(event.target.value)}
              placeholder="Search by name, email, company or phone"
              aria-label="Search leads"
              className="h-9 pl-9"
            />
          </div>
          <div className="flex flex-wrap gap-2">
            <Button
              variant="secondary"
              size="sm"
              className="h-9"
              onClick={() => setFiltersOpen(true)}
              aria-haspopup="dialog"
            >
              <SlidersHorizontal /> Filter{activeFilterCount ? ` (${activeFilterCount})` : ""}
            </Button>
            <MarkAllReviewedButton
              filters={filters}
              unreviewed={unreviewedTotal}
              filtered={activeFilterCount > 0 || Boolean(search.q)}
            />
            <Select
              value={sort}
              onValueChange={(value) =>
                setSearch({ sort: value === "priority" ? undefined : (value as Search["sort"]) })
              }
            >
              <SelectTrigger aria-label="Sort" className="h-9 w-auto min-w-40">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {leadSorts.map((value) => (
                  <SelectItem key={value} value={value}>
                    Sort: {sortLabels[value]}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>
        <ActiveFilterChips
          value={applied}
          stages={stageList}
          services={serviceList}
          onRemove={(key) => void setSearch({ [key]: undefined })}
          onClearAll={() => {
            setQuery("");
            void navigate({ search: sort === "priority" ? {} : { sort }, replace: true });
          }}
        />
      </Card>

      <FilterDialog
        open={filtersOpen}
        onOpenChange={setFiltersOpen}
        value={applied}
        onApply={(next) => applyFilters({ ...next })}
        stages={stageList}
        services={serviceList}
      />

      <Card padding="none" className="overflow-hidden">
        {leads.isPending ? (
          <div className="flex flex-col gap-3 p-5">
            {Array.from({ length: 6 }, (_, i) => (
              <Skeleton key={i} className="h-12" />
            ))}
          </div>
        ) : leads.isError ? (
          <EmptyState
            icon={<ListChecks />}
            title="Couldn't load leads"
            description={errorMessage(leads.error)}
          />
        ) : rows.length === 0 ? (
          <EmptyState
            icon={<ListChecks />}
            title={activeFilterCount || search.q ? "No leads match" : "No leads yet"}
            description={
              activeFilterCount || search.q
                ? "Try removing a filter."
                : "Enquiries from your form will appear here. Share the link from Settings → Lead form."
            }
          />
        ) : (
          <>
            <div className="hidden md:block">
              <Table>
                <TableHeader>
                  <TableRow className="hover:bg-transparent">
                    <TableHead className="w-14">#</TableHead>
                    <TableHead>Lead</TableHead>
                    <TableHead>Stage</TableHead>
                    <TableHead className="hidden 2xl:table-cell">Service</TableHead>
                    <TableHead className="hidden lg:table-cell">Source</TableHead>
                    <TableHead className="hidden 2xl:table-cell">Tags</TableHead>
                    <TableHead className="hidden lg:table-cell">Assigned</TableHead>
                    <TableHead>Follow-up</TableHead>
                    <TableHead className="hidden 2xl:table-cell">Received</TableHead>
                    <TableHead className="w-12">
                      <span className="sr-only">Actions</span>
                    </TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {rows.map((lead, index) => (
                    <TableRow
                      key={lead.id}
                      className="cursor-pointer"
                      data-unreviewed={lead.reviewedByMe ? undefined : ""}
                      onClick={() =>
                        void navigate({ to: "/leads/$leadId", params: { leadId: lead.id } })
                      }
                    >
                      <TableCell data-tabular className="text-fg-muted">
                        {lead.priority}
                      </TableCell>
                      <TableCell>
                        <span className="flex items-center gap-1">
                          {!lead.reviewedByMe && <UnreviewedDot className="-ml-1" />}
                          <LeadPeek lead={lead}>
                            <Link
                              to="/leads/$leadId"
                              params={{ leadId: lead.id }}
                              className={cn(
                                "text-fg hover:text-primary",
                                lead.reviewedByMe ? "font-medium" : "font-bold",
                              )}
                              onClick={(event) => event.stopPropagation()}
                            >
                              <Highlight text={fullName(lead)} query={search.q} />
                            </Link>
                          </LeadPeek>
                        </span>
                        <div className="flex items-center gap-2 text-small text-fg-muted">
                          <span className="truncate">
                            <Highlight text={lead.company ?? lead.email} query={search.q} />
                          </span>
                          <EmailFlagBadge flag={lead.emailFlag} />
                        </div>
                        <HiddenMatches lead={lead} query={search.q} emailShown={!lead.company} />
                      </TableCell>
                      <TableCell>
                        <StageBadge color={lead.stage.color as StageColor}>
                          {lead.stage.name}
                        </StageBadge>
                      </TableCell>
                      <TableCell className="hidden max-w-48 truncate text-fg-secondary 2xl:table-cell">
                        {lead.service?.name ?? "—"}
                      </TableCell>
                      <TableCell className="hidden lg:table-cell">
                        <ChannelBadge channel={lead.sourceChannel} campaign={lead.utmCampaign} />
                      </TableCell>
                      <TableCell className="hidden max-w-72 2xl:table-cell">
                        <CampaignTags
                          campaign={lead.campaign}
                          onCampaign={(campaignId) => void setSearch({ campaignId })}
                          onKeyword={(keyword) => void setSearch({ keyword })}
                        />
                      </TableCell>
                      <TableCell className="hidden lg:table-cell">
                        {lead.assignees.length ? (
                          <AssigneeAvatars assignees={lead.assignees} />
                        ) : (
                          <span className="text-fg-muted">—</span>
                        )}
                      </TableCell>
                      <TableCell>
                        <FollowUpBadge day={lead.nextFollowUpOn} compact />
                      </TableCell>
                      <TableCell className="hidden text-fg-secondary 2xl:table-cell">
                        {formatDate(lead.createdAt)}
                      </TableCell>
                      <TableCell onClick={(event) => event.stopPropagation()}>
                        <RowActions
                          lead={lead}
                          canReorder={canReorder}
                          isFirst={index === 0}
                          isLast={index === rows.length - 1}
                          onReorder={(to) => reorder(lead, to)}
                          onSendEmail={onSendEmail}
                          onToggleReview={() =>
                            (lead.reviewedByMe ? review.markUnreviewed : review.markReviewed).mutate(
                              lead.id,
                            )
                          }
                          onAssign={onAssign}
                          onDelete={onDelete}
                        />
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>

            <ul className="flex flex-col divide-y divide-border md:hidden">
              {rows.map((lead, index) => (
                <li key={lead.id} className="flex items-start gap-3 p-4">
                  <span data-tabular className="mt-0.5 w-6 shrink-0 text-small text-fg-muted">
                    {lead.priority}
                  </span>
                  <Link to="/leads/$leadId" params={{ leadId: lead.id }} className="min-w-0 flex-1">
                    <p
                      className={cn(
                        "flex items-center gap-1 truncate text-fg",
                        lead.reviewedByMe ? "font-medium" : "font-bold",
                      )}
                    >
                      {!lead.reviewedByMe && <UnreviewedDot className="-ml-1" />}
                      <Highlight text={fullName(lead)} query={search.q} />
                    </p>
                    <p className="truncate text-small text-fg-muted">
                      <Highlight text={lead.company ?? lead.email} query={search.q} />
                    </p>
                    <HiddenMatches lead={lead} query={search.q} emailShown={!lead.company} />
                    <div className="mt-2 flex flex-wrap gap-1.5">
                      <StageBadge color={lead.stage.color as StageColor}>
                        {lead.stage.name}
                      </StageBadge>
                      <FollowUpBadge day={lead.nextFollowUpOn} />
                      <EmailFlagBadge flag={lead.emailFlag} />
                      <AssigneeAvatars assignees={lead.assignees} />
                    </div>
                  </Link>
                  <RowActions
                    lead={lead}
                    canReorder={canReorder}
                    isFirst={index === 0}
                    isLast={index === rows.length - 1}
                    onReorder={(to) => reorder(lead, to)}
                    onSendEmail={onSendEmail}
                    onToggleReview={() =>
                      (lead.reviewedByMe ? review.markUnreviewed : review.markReviewed).mutate(
                        lead.id,
                      )
                    }
                    onAssign={onAssign}
                    onDelete={onDelete}
                  />
                </li>
              ))}
            </ul>
          </>
        )}
        {leads.hasNextPage && (
          <div className="flex justify-center border-t border-border p-3">
            <Button
              variant="ghost"
              loading={leads.isFetchingNextPage}
              onClick={() => leads.fetchNextPage()}
            >
              Show more
            </Button>
          </div>
        )}
      </Card>

      <LeadFormDialog open={addOpen} onOpenChange={setAddOpen} />
      <AssignLeadDialog lead={assigning} onOpenChange={(open) => !open && setAssigning(null)} />
      {composeDialog}
      {deleting && (
        <ConfirmDialog
          open
          onOpenChange={(open) => !open && setDeleting(null)}
          title={`Delete ${fullName(deleting)}?`}
          description="The lead disappears from Purlink. Their emails stay in the email history."
          confirmLabel="Delete lead"
          destructive
          loading={remove.isPending}
          onConfirm={() => remove.mutate(deleting)}
        />
      )}
    </>
  );
}

function RowActions({
  lead,
  canReorder,
  isFirst,
  isLast,
  onReorder,
  onSendEmail,
  onToggleReview,
  onAssign,
  onDelete,
}: {
  lead: LeadSummary;
  canReorder: boolean;
  isFirst: boolean;
  isLast: boolean;
  onReorder: (to: "top" | "up" | "down" | "bottom") => void;
  /** Absent when the user can't send email. */
  onSendEmail: ((lead: LeadSummary) => void) | undefined;
  onToggleReview: () => void;
  /** Absent unless the user can assign leads (Super Admins). */
  onAssign: ((lead: LeadSummary) => void) | undefined;
  /** Absent unless the user can delete leads (Super Admins). */
  onDelete: ((lead: LeadSummary) => void) | undefined;
}) {
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button variant="ghost" size="icon-sm" aria-label={`Actions for ${fullName(lead)}`}>
          <MoreHorizontal />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-52">
        <DropdownMenuItem asChild>
          <Link to="/leads/$leadId" params={{ leadId: lead.id }}>
            <ExternalLink /> Open lead
          </Link>
        </DropdownMenuItem>
        {onSendEmail && (
          <DropdownMenuItem onSelect={() => onSendEmail(lead)}>
            <Send /> Send email
          </DropdownMenuItem>
        )}
        <DropdownMenuItem onSelect={onToggleReview}>
          {lead.reviewedByMe ? (
            <>
              <EyeOff /> Mark as unreviewed
            </>
          ) : (
            <>
              <Eye /> Mark as reviewed
            </>
          )}
        </DropdownMenuItem>
        {onAssign && (
          <DropdownMenuItem onSelect={() => onAssign(lead)}>
            <UserPlus /> Assign…
          </DropdownMenuItem>
        )}
        {canReorder && (
          <>
            <DropdownMenuSeparator />
            <DropdownMenuItem disabled={isFirst} onSelect={() => onReorder("top")}>
              <ArrowUpToLine /> Move to top
            </DropdownMenuItem>
            <DropdownMenuItem disabled={isFirst} onSelect={() => onReorder("up")}>
              <ChevronUp /> Move up
            </DropdownMenuItem>
            <DropdownMenuItem disabled={isLast} onSelect={() => onReorder("down")}>
              <ChevronDown /> Move down
            </DropdownMenuItem>
            <DropdownMenuItem disabled={isLast} onSelect={() => onReorder("bottom")}>
              <ArrowDownToLine /> Move to bottom
            </DropdownMenuItem>
          </>
        )}
        {onDelete && (
          <>
            <DropdownMenuSeparator />
            <DropdownMenuItem destructive onSelect={() => onDelete(lead)}>
              <Trash2 /> Delete lead
            </DropdownMenuItem>
          </>
        )}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
